import React, { useEffect, useState, useCallback } from 'react';
import { authManager } from '../utils/auth';

const Insider_Dashboard = () => {
    const [counts, setCounts] = useState({
        totalBrands: 0,
        totalProducts: 0,
        cj: { brands: 0, products: 0 },
        impact: { brands: 0, products: 0 }
    });

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);

    const fetchDashboardCounts = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await authManager.authenticatedFetch(
                `${process.env.REACT_APP_API_BASE_URL}/shopping/brands/admin/dashboard-count`
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            if (data?.result) {
                setCounts(data.result);
                setRetryCount(0); 
            } else {
                throw new Error('Invalid response format');
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setError(error.message || 'Failed to load dashboard data');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const handleRetry = () => {
        if (retryCount < 3) {
            setRetryCount(prev => prev + 1);
            fetchDashboardCounts();
        }
    };

    useEffect(() => {
        fetchDashboardCounts();
    }, [fetchDashboardCounts]);

    const ErrorDisplay = () => (
        <div className="text-center py-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
                <div className="text-red-600 mb-4">
                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <h3 className="text-lg font-medium text-red-800 mb-2">Unable to Load Dashboard</h3>
                <p className="text-red-600 mb-4">{error}</p>
                {retryCount < 3 && (
                    <button
                        onClick={handleRetry}
                        className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Retrying...' : `Retry ${retryCount > 0 ? `(${retryCount}/3)` : ''}`}
                    </button>
                )}
                {retryCount >= 3 && (
                    <p className="text-sm text-red-500">Please refresh the page or contact support</p>
                )}
            </div>
        </div>
    );

    const formatNumber = (num) => {
        if (typeof num !== 'number' || isNaN(num)) return '0';
        return num.toLocaleString();
    };

    if (isLoading && !error) {
        return (
            <div className="content">
                <div className="container-fluid text-center py-5">
                    <div className="spinner-border text-primary" role="status" style={{ alignItems:'center' }}>
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }


    if (error && !isLoading) {
        return (
            <div>
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-6">
                                <h1 className="m-0 txt_lft">MMI Dashboard</h1>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="content">
                    <div className="container-fluid">
                        <ErrorDisplay />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0 txt_lft">MMI Dashboard</h1>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    {isLoading && (
                        <div className="text-center mb-3">
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                        </div>
                    )}

                    <div className="row justify-content-center">
                        <div className="col-12 col-md-6 col-lg-3 mb-4">
                            <a href="/brands" className={isLoading ? 'pointer-events-none' : ''}>
                                <div className="card">
                                    <div className="card-header bg_primary">
                                        <div className="w-100 text-center f_sz18 fwt_500 f_white">Total Brands</div>
                                    </div>
                                    <div className="card-body d-flex text-center align-items-center">
                                        <div className="col-12">
                                            <h3 className="f_black">{formatNumber(counts.totalBrands)}</h3>
                                        </div>
                                    </div>
                                </div>
                            </a>
                        </div>
                        <div className="col-12 col-md-6 col-lg-3 mb-4">
                            <a href="/products" className={isLoading ? 'pointer-events-none' : ''}>
                                <div className="card">
                                    <div className="card-header bg_primary">
                                        <div className="w-100 text-center f_sz18 fwt_500 f_white">Total Products</div>
                                    </div>
                                    <div className="card-body d-flex text-center align-items-center">
                                        <div className="col-12">
                                            <h3 className="f_black">{formatNumber(counts.totalProducts)}</h3>
                                        </div>
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>

                    <div className="row justify-content-center">
                        <h3 className="my-5">Affiliate Platforms</h3>
                    </div>
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-6 col-lg-3 mb-4">
                            <div className="card card-outline card-primary">
                                <div className="card-header">
                                    <div className="w-100 text-center f_sz18 fwt_500 f_blue ">CJ</div>
                                </div>
                                <div className="card-body d-flex text-center">
                                    <div className="col-6">
                                        <h4 className="card-title mb-4 w-100">Brands</h4>
                                        <h3 className="f_black">{formatNumber(counts.cj?.brands)}</h3>
                                    </div>
                                    <div className="col-6">
                                        <h4 className="card-title mb-4 w-100">Products</h4>
                                        <h3 className="f_black">{formatNumber(counts.cj?.products)}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-md-6 col-lg-3 mb-4">
                            <div className="card card-outline card-primary">
                                <div className="card-header">
                                    <div className="w-100 text-center f_sz18 fwt_500 f_blue">Impact</div>
                                </div>
                                <div className="card-body d-flex text-center">
                                    <div className="col-6">
                                        <h4 className="card-title mb-4 w-100">Brands</h4>
                                        <h3 className="f_black">{formatNumber(counts.impact?.brands)}</h3>
                                    </div>
                                    <div className="col-6">
                                        <h4 className="card-title mb-4 w-100">Products</h4>
                                        <h3 className="f_black">{formatNumber(counts.impact?.products)}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-md-6 col-lg-3 mb-4">
                            <div className="card card-outline card-primary">
                                <div className="card-header">
                                    <div className="w-100 text-center f_sz18 fwt_500 f_blue">Rakuten Advertising</div>
                                </div>
                                <div className="card-body d-flex text-center">
                                    <div className="col-6">
                                        <h4 className="card-title mb-4 w-100">Brands</h4>
                                        <h3 className="f_black">0</h3>
                                    </div>
                                    <div className="col-6">
                                        <h4 className="card-title mb-4 w-100">Products</h4>
                                        <h3 className="f_black">0</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Insider_Dashboard;